import { Badge, Button, Tab, TabList, Text } from '@fluentui/react-components';
import { Bug24Regular, Play48Regular } from '@fluentui/react-icons';
import { useEffect, useRef, useState } from 'react';

import type { ResponsePanelProps } from './ResponsePanel.interface.js';
import { useStyles } from './ResponsePanel.style.js';

function getStatusColor(
  status: number,
): 'success' | 'warning' | 'danger' | 'informative' {
  if (status >= 200 && status < 300) return 'success';
  if (status >= 300 && status < 400) return 'informative';
  if (status >= 400 && status < 500) return 'warning';
  return 'danger';
}

function prettifyJson(body: string): string {
  try {
    return JSON.stringify(JSON.parse(body) as unknown, null, 2);
  } catch {
    return body;
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function useElapsedTimer(active: boolean): number {
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    if (active) {
      startRef.current = Date.now();
      setElapsed(0);
      const id = setInterval(() => {
        setElapsed(Date.now() - (startRef.current ?? Date.now()));
      }, 100);
      return () => clearInterval(id);
    } else {
      startRef.current = null;
      return undefined;
    }
  }, [active]);

  return elapsed;
}

export function ResponsePanel({
  result,
  loading,
  varErrors = [],
  onDebug,
  onCancel,
}: ResponsePanelProps) {
  const styles = useStyles();
  const [activeTab, setActiveTab] = useState<'response' | 'headers'>(
    'response',
  );
  const elapsed = useElapsedTimer(loading);

  const bodySize = result
    ? new TextEncoder().encode(result.body).byteLength
    : 0;

  function handleDebugClick() {
    if (result?.cv && onDebug) {
      const cv = result.cv.split('.')[0] ?? result.cv;
      onDebug(cv);
    }
  }

  return (
    <div className={styles.root}>
      {(loading || result) && (
        <div className={styles.tabBar}>
          <TabList
            className={styles.tabList}
            selectedValue={activeTab}
            onTabSelect={(_, d) =>
              setActiveTab(d.value as 'response' | 'headers')
            }
            size="small"
          >
            <Tab value="response">Response</Tab>
            <Tab value="headers">Headers</Tab>
          </TabList>

          <div className={styles.meta}>
            {!loading && result && (
              <>
                <Badge
                  color={getStatusColor(result.status)}
                  appearance="filled"
                  size="small"
                >
                  {result.status} {result.statusText}
                </Badge>
                <Text className={styles.metaText}>
                  {formatMs(result.durationMs)}
                </Text>
                <Text className={styles.metaText}>{formatBytes(bodySize)}</Text>
              </>
            )}
          </div>
        </div>
      )}

      <div className={styles.contentWrapper}>
        {loading && (
          <>
            <div className={styles.overlayBackdrop} />
            <div className={styles.overlayContent}>
              <span
                className={styles.overlayTimer}
                style={{ fontSize: '2.5rem' }}
              >
                {formatMs(elapsed)}
              </span>
              {onCancel && (
                <Button
                  appearance="secondary"
                  size="small"
                  onClick={onCancel}
                  style={{ marginTop: '12px' }}
                >
                  Cancel Request
                </Button>
              )}
            </div>
          </>
        )}

        <div className={styles.content}>
          {varErrors.length > 0 && (
            <pre className={styles.pre} style={{ color: 'red' }}>
              {varErrors.join('\n')}
            </pre>
          )}
          {!loading && !result && varErrors.length === 0 && (
            <div className={styles.placeholder}>
              <Play48Regular />
              <Text className={styles.placeholderText}>
                Send a request to view the response here
              </Text>
            </div>
          )}
          {result && (
            <>
              {result.cv && (
                <div className={styles.cvRow}>
                  <span className={styles.cvLabel}>CV</span>
                  <span className={styles.cvValue}>{result.cv}</span>
                  {onDebug && (
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<Bug24Regular />}
                      onClick={handleDebugClick}
                    >
                      Debug
                    </Button>
                  )}
                </div>
              )}
              {activeTab === 'response' && (
                <pre className={styles.pre}>{prettifyJson(result.body)}</pre>
              )}
              {activeTab === 'headers' && (
                <div>
                  <div className={styles.headerRow}>
                    <span className={styles.headerKey}>Name</span>
                    <span className={styles.headerValue}>Value</span>
                  </div>
                  {Object.entries(result.headers).map(([k, v]) => (
                    <div key={k} className={styles.headerRow}>
                      <span className={styles.headerKey}>{k}</span>
                      <span className={styles.headerValue}>{v}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
